import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { toast } from '../components/Toast';
import { Pill } from '../ui/Pill';
import { Icon } from '../ui/Icons';
import type { Lead, LeadStatus } from '../lib/types';

type Stage = 'all' | Lowercase<LeadStatus>;

const STATUSES: LeadStatus[] = ['NEW', 'CONTACTED', 'QUALIFIED', 'WON', 'LOST'];

const STAGES: { id: Lowercase<LeadStatus>; label: string; tone: 'new' | 'contacted' | 'qualified' | 'won' | 'lost'; bar: string }[] = [
  { id: 'new',       label: 'New',       tone: 'new',       bar: '#F59E0B' },
  { id: 'contacted', label: 'Contacted', tone: 'contacted', bar: '#3B82F6' },
  { id: 'qualified', label: 'Qualified', tone: 'qualified', bar: '#8B5CF6' },
  { id: 'won',       label: 'Won',       tone: 'won',       bar: '#10B981' },
  { id: 'lost',      label: 'Lost',      tone: 'lost',      bar: '#EF4444' },
];

export const Leads = () => {
  const qc = useQueryClient();
  const [stage, setStage] = useState<Stage>('all');
  const [search, setSearch] = useState('');

  const q = useQuery<{ leads: Lead[] }>({
    queryKey: ['leads'],
    queryFn: async () => (await api.get('/api/tenant/leads?limit=100')).data,
  });

  const mut = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: LeadStatus }) =>
      (await api.patch(`/api/tenant/leads/${id}`, { status })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Status updated');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const allLeads = q.data?.leads ?? [];
  const counts = useMemo(() => {
    const c: Record<Lowercase<LeadStatus>, number> = { new: 0, contacted: 0, qualified: 0, won: 0, lost: 0 };
    for (const l of allLeads) c[l.status.toLowerCase() as Lowercase<LeadStatus>] += 1;
    return c;
  }, [allLeads]);
  const maxCount = Math.max(1, ...Object.values(counts));

  const wonCount = counts.won;
  const total = allLeads.length;
  const conversionRate = total > 0 ? ((wonCount / total) * 100).toFixed(1) : '0.0';

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allLeads.filter((l) => {
      if (stage !== 'all' && l.status.toLowerCase() !== stage) return false;
      if (!term) return true;
      return (
        (l.name?.toLowerCase().includes(term) ?? false) ||
        (l.email?.toLowerCase().includes(term) ?? false) ||
        (l.phone?.toLowerCase().includes(term) ?? false) ||
        (l.notes?.toLowerCase().includes(term) ?? false)
      );
    });
  }, [allLeads, stage, search]);

  return (
    <div>
      <PageHeader
        title="Leads"
        subtitle="People your agent has captured. Use the inline dropdown to update status."
        actions={
          <>
            <button type="button" className="btn-ghost" disabled title="Coming soon">
              <Icon.ArrowUpRight size={14} />
              Export CSV
            </button>
          </>
        }
      />

      <div className="px-10 pb-10 flex flex-col gap-[18px]">
        {/* Pipeline */}
        <section className="card overflow-hidden">
          <div className="px-[22px] pt-[18px] pb-3.5 flex items-center justify-between">
            <div>
              <div className="text-[13.5px] font-semibold text-ink">Pipeline · this month</div>
              <div className="text-[11.5px] text-ink-subtle mt-0.5">
                {total} {total === 1 ? 'lead' : 'leads'} · conversion {conversionRate}%
              </div>
            </div>
            {wonCount > 0 && <Pill tone="accent">{wonCount} closed</Pill>}
          </div>
          <div className="px-[22px] pb-[22px] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {STAGES.map((s) => {
              const count = counts[s.id];
              const pct = Math.round((count / maxCount) * 100);
              return (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => setStage(stage === s.id ? 'all' : s.id)}
                  className={`text-left bg-[#FAFAFB] border rounded-[11px] px-3.5 py-3 transition-colors ${
                    stage === s.id ? 'border-ink' : 'border-line hover:border-line-strong'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <Pill tone={s.tone} dot>{s.label}</Pill>
                    <span className="text-lg font-serif font-normal text-ink leading-none">{count}</span>
                  </div>
                  <div className="h-1 bg-white rounded-full mt-2.5 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${pct}%`, background: s.bar }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Table */}
        <section className="card overflow-hidden">
          <div className="px-[22px] py-3.5 border-b border-line flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1.5 px-2.5 py-1.5 border border-line rounded-lg w-[240px]">
                <Icon.Search size={13} />
                <input
                  type="search"
                  className="flex-1 bg-transparent border-0 outline-none text-xs text-ink placeholder:text-ink-subtle"
                  placeholder="Search by name, email, note…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              <div className="flex gap-1 text-[11px]">
                {(['all', ...STAGES.map((s) => s.id)] as Stage[]).map((s) => {
                  const on = stage === s;
                  const label = s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStage(s)}
                      className={`px-2.5 py-1 rounded-full transition-colors ${
                        on
                          ? 'bg-ink text-white'
                          : 'bg-transparent text-ink-muted border border-line-strong hover:text-ink'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <span className="text-[11.5px] text-ink-subtle">
              Showing {filtered.length} of {total}
            </span>
          </div>

          {q.isLoading ? (
            <div className="p-10 text-center text-[12.5px] text-ink-subtle">Loading…</div>
          ) : q.error ? (
            <div className="p-10 text-center text-[12.5px] text-rose-700">{errorMessage(q.error)}</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[12.5px] text-ink-subtle">
              {total === 0
                ? 'No leads captured yet — they will show up here once your agent qualifies a visitor.'
                : 'No leads match these filters.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[12.5px]">
                <thead>
                  <tr>
                    {['Captured', 'Contact', 'Note', 'Source', 'Status'].map((h) => (
                      <th
                        key={h}
                        className="text-left px-3.5 py-2.5 text-[10.5px] font-medium uppercase tracking-[0.1em] text-ink-subtle border-b border-line bg-[#FAFAFB]"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l, i) => {
                    const initials = (l.name ?? l.email ?? '?')
                      .split(/\s+|@|\./)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((p) => p[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);
                    return (
                      <tr
                        key={l.id}
                        className={i === filtered.length - 1 ? '' : 'border-b border-line'}
                      >
                        <td className="px-3.5 py-3 text-ink-muted whitespace-nowrap tabular-nums">
                          {formatDate(l.createdAt)}
                        </td>
                        <td className="px-3.5 py-3">
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 ${
                                l.name ? 'bg-[#F2F2F5] text-ink-soft' : 'bg-amber-50 text-amber-900'
                              }`}
                            >
                              {l.name ? initials : '?'}
                            </div>
                            <div className="min-w-0">
                              <div className="text-ink font-medium leading-tight truncate">
                                {l.name ?? (
                                  <span className="text-ink-subtle italic">(unnamed)</span>
                                )}
                              </div>
                              <div className="text-[11px] text-ink-subtle mt-px flex flex-wrap gap-x-2 items-center">
                                {l.email && (
                                  <a
                                    href={`mailto:${l.email}`}
                                    className="inline-flex items-center gap-1 text-accent hover:underline"
                                  >
                                    <Icon.Mail size={10} />
                                    {l.email}
                                  </a>
                                )}
                                {l.phone && (
                                  <span className="inline-flex items-center gap-1 text-ink-muted">
                                    {l.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td
                          className="px-3.5 py-3 text-ink-muted max-w-[260px] truncate"
                          title={l.notes ?? ''}
                        >
                          {l.notes ?? '—'}
                        </td>
                        <td className="px-3.5 py-3">
                          {l.conversationId ? (
                            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-accent font-mono">
                              <Icon.Chat size={11} />
                              v-{l.conversationId.slice(0, 6)}
                            </span>
                          ) : (
                            <span className="text-ink-subtle">—</span>
                          )}
                        </td>
                        <td className="px-3.5 py-3">
                          <StatusSelect
                            current={l.status}
                            onChange={(s) => mut.mutate({ id: l.id, status: s })}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  const same = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(same ? {} : { year: 'numeric' }),
  });
};

const StatusSelect = ({
  current,
  onChange,
}: {
  current: LeadStatus;
  onChange: (s: LeadStatus) => void;
}) => {
  const tone = current.toLowerCase() as 'new' | 'contacted' | 'qualified' | 'won' | 'lost';
  return (
    <label className="inline-flex items-center gap-1.5 py-1 pl-2 pr-1.5 rounded-md border border-line-strong bg-white cursor-pointer hover:border-ink-subtle transition-colors">
      <Pill tone={tone} dot>{current.toLowerCase()}</Pill>
      <select
        value={current}
        onChange={(e) => onChange(e.target.value as LeadStatus)}
        aria-label="Status"
        className="appearance-none bg-transparent border-0 outline-none text-[10px] text-ink-subtle cursor-pointer pr-3"
        style={{ backgroundImage: 'none' }}
      >
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {s.toLowerCase()}
          </option>
        ))}
      </select>
    </label>
  );
};
